// Global Instructions Rule Applied!
//
// Deal Pipeline → Budget tab (owner 2026-11-03, Option A "statement"): the
// capital and cash position of the funds the user follows, as one table —
// the line items as rows in statement order, one column per fund plus a
// Total. The open deal commitments are sub-rows under Commitments, so a deal
// two funds are both in sits on one row with a figure in each column.
//
//   Committed Capital / Called Capital / Uncalled Capital   (capital-call position)
//   one line per configured GL account / Net Cash            (the ledger)
//   Commitments: one sub-row per committed deal              (deal_commitments)
//   Cash Remaining = Net Cash - Commitments
//   Capital Remaining = Uncalled + Net Cash - Commitments
//
// The Cash lines are cfg.deal_pipeline_budget_accounts (Configuration →
// Pipeline Budget), each the account's balance to date DEBIT-POSITIVE, so
// Net Cash is the plain sum of the lines above it: a payable the fund owes
// shows negative and deducts, one in debit (the fund is owed money) adds.
//
// Chrome: the ViewToolbar ribbon with the fund multi-select in its leftSlot
// (per-user pick, cfg default on first open), the standard refresh; one
// FormSection holding an antd <Table size='small'> (the CommitmentsTab
// precedent). Figures render through NumberText.
//
// Layout (owner 2026-11-04, option C "statement style"): read like a printed
// balance sheet. The FormSection is flush and the table's header row IS its
// section bar - title and as-of on the left, the fund names over their
// columns. No block label rows: a thin gap and the bold subtotal close each
// block, and the block's help sits on that subtotal. Rows are tight (a scoped
// Table theme) and figures are whole units through the cfg
// 'currency_whole' number format, so a figure that had cents keeps
// NumberText's dotted underline with the exact amount.

import React, { useCallback, useMemo } from 'react'
import { ConfigProvider, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import { FormSection, FormSectionTitle } from '../../../../core/components/FormSection.tsx'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import NumberText from '../../../../core/components/NumberText.tsx'
import HelpTip from '../../../../core/components/HelpTip.tsx'
import { TABLE_DEALS } from '../../../../state_manager/globals.ts'
import dayjs from 'dayjs'
import type {
  DealPipelineBudgetAccount,
  DealPipelineBudgetFigureKey,
  DealPipelineBudgetFund
} from '../../../../state_manager/data_models/deal_pipeline_budget.ts'
import { useDealPipelineBudget } from '../../hooks/useDealPipelineBudget.ts'

const { Text } = Typography

const formatDate = (value: string): string => dayjs(value).format('MM/DD/YYYY')

// whole units: the cents only spread a summary out (cfg.number_formats)
const FIGURE_FORMAT_KEY = 'currency_whole'

// tight statement rows, and the header row drawn as the FormSection bar
// (bg-gray-100 / text-gray-700, no column dividers). fontSize is a TOKEN, not
// only the Table's: the bold subtotals and the dashes are antd Text, which
// would stay at 14px and stretch their rows past the plain ones
const STATEMENT_TABLE_THEME = {
  token: { fontSize: 13 },
  components: {
    Table: {
      cellPaddingBlockSM: 2,
      headerBg: '#f3f4f6',
      headerColor: '#374151',
      headerSplitColor: 'transparent',
      headerBorderRadius: 0
    }
  }
}
const SECTION_BAR_CELL_STYLE: React.CSSProperties = { height: 32, paddingTop: 0, paddingBottom: 0, fontSize: 12 }
const GAP_CELL_STYLE: React.CSSProperties = { height: 6, padding: 0, borderBottom: 0 }

type RowKind = 'gap' | 'figure' | 'account' | 'subtotal' | 'deal' | 'remaining' | 'hero'

interface BudgetRow {
  key: string
  kind: RowKind
  label: string
  help?: string
  /** For figure rows: the key read off each fund entry and the totals. */
  figure?: DealPipelineBudgetFigureKey
  /** For account rows: the configured cash line read off each fund entry. */
  accountId?: string
  /** For deal rows: the amount per fund id, and the deal to open. */
  dealId?: string
  amounts?: Record<string, number>
  total?: number
}

const CASH_REMAINING_HELP = 'Net Cash less the open commitments. Negative means the fund has to call capital before it can fund what it has committed.'
const CAPITAL_REMAINING_HELP = 'Uncalled Capital plus Net Cash, less the open commitments: what the fund can still put to work.'
const CASH_HELP = 'The GL accounts set in Configuration → Pipeline Budget, each at its ledger balance to date. Net Cash is their sum: a payable the fund owes shows negative and is deducted; a payable in debit (the fund is owed money) shows positive and is added.'
const COMMITMENTS_HELP = 'Deals in the Committed stage of the origination pipeline, with their commitment to each fund. A booked deal moves to Portfolio and drops out.'
const FUNDS_HELP = 'The funds this tab shows, remembered for you. Clear the pick to go back to the default funds.'

export default function DealPipelineBudgetTab(): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const { budget, loading, selectedFundIds, setSelectedFundIds, refresh } = useDealPipelineBudget()
  const activateRef = useRefreshOnActivate(() => refresh(true))

  const funds: DealPipelineBudgetFund[] = useMemo(() => budget?.funds ?? [], [budget])
  const accounts: DealPipelineBudgetAccount[] = useMemo(() => budget?.accounts ?? [], [budget])
  const commitments = useMemo(() => budget?.commitments ?? [], [budget])
  const totals = budget?.totals

  const rows: BudgetRow[] = useMemo(() => {
    const figure = (key: string, kind: RowKind, label: string, fig: DealPipelineBudgetFigureKey, help?: string): BudgetRow => ({
      key,
      kind,
      label,
      figure: fig,
      help
    })
    // one sub-row per deal; a deal two funds share collapses onto one row
    const byDeal = new Map<string, BudgetRow>()
    for (const c of commitments) {
      const amount = c.deal_commitments__amount ?? 0
      const existing = byDeal.get(c.deals__id)
      if (existing) {
        existing.amounts![c.funds__id] = (existing.amounts![c.funds__id] ?? 0) + amount
        existing.total = (existing.total ?? 0) + amount
        continue
      }
      byDeal.set(c.deals__id, {
        key: `deal:${c.deals__id}`,
        kind: 'deal',
        label: c.deals__name || 'Deal',
        dealId: c.deals__id,
        amounts: { [c.funds__id]: amount },
        total: amount
      })
    }
    const dealRows = [...byDeal.values()].sort((a, b) => a.label.localeCompare(b.label))
    const accountRows: BudgetRow[] = accounts.map((a) => ({
      key: `account:${a.deal_pipeline_budget_accounts__id}`,
      kind: 'account',
      label: a.deal_pipeline_budget_accounts__label || a.acc_gl_accounts__account_name || 'Account',
      accountId: a.deal_pipeline_budget_accounts__id,
      total: a.prog__total ?? 0
    }))

    return [
      figure('committed', 'figure', 'Committed Capital', 'prog__committed'),
      figure('called', 'figure', 'Called Capital', 'prog__called'),
      figure('uncalled', 'subtotal', 'Uncalled Capital', 'prog__uncalled'),
      { key: 'gap:cash', kind: 'gap', label: '' },
      ...accountRows,
      figure('net_cash', 'subtotal', 'Net Cash', 'prog__net_cash', CASH_HELP),
      { key: 'gap:commitments', kind: 'gap', label: '' },
      ...dealRows,
      figure('commitments', 'subtotal', 'Total Commitments', 'prog__commitments', COMMITMENTS_HELP),
      { key: 'gap:remaining', kind: 'gap', label: '' },
      figure('cash_remaining', 'remaining', 'Cash Remaining', 'prog__cash_remaining', CASH_REMAINING_HELP),
      figure('capital_remaining', 'hero', 'Capital Remaining', 'prog__capital_remaining', CAPITAL_REMAINING_HELP)
    ]
  }, [accounts, commitments])

  const valueOf = useCallback(
    (row: BudgetRow, fundId: string | null): number | null => {
      if (row.kind === 'gap') return null
      if (row.kind === 'deal') {
        if (fundId === null) return row.total ?? 0
        return row.amounts?.[fundId] ?? null
      }
      if (row.kind === 'account') {
        if (fundId === null) return row.total ?? 0
        const fund = funds.find((f) => f.funds__id === fundId)
        const line = fund?.accounts.find((l) => l.deal_pipeline_budget_accounts__id === row.accountId)
        return line?.prog__balance ?? 0
      }
      const source = fundId === null ? totals : funds.find((f) => f.funds__id === fundId)
      const value = row.figure ? source?.[row.figure] : null
      return value ?? 0
    },
    [funds, totals]
  )

  const renderFigure = useCallback(
    (row: BudgetRow, fundId: string | null): React.ReactNode => {
      const value = valueOf(row, fundId)
      if (value === null) return row.kind === 'deal' ? <Text type='secondary'>—</Text> : null
      const strong = row.kind === 'subtotal' || row.kind === 'remaining' || row.kind === 'hero'
      const danger = row.kind === 'remaining' && value < 0
      return (
        <NumberText
          value={value}
          formatKey={FIGURE_FORMAT_KEY}
          wrap={(n) => (danger ? <Text strong type='danger'>{n}</Text> : strong ? <Text strong>{n}</Text> : n)}
        />
      )
    },
    [valueOf]
  )

  const asOf = budget?.as_of ? formatDate(budget.as_of) : null

  const columns: ColumnsType<BudgetRow> = useMemo(() => {
    const figureCell = (row: BudgetRow) => (row.kind === 'gap' ? { colSpan: 0 } : {})
    const labelColumn: ColumnsType<BudgetRow>[number] = {
      title: (
        <FormSectionTitle
          title='Capital position'
          titleColor='blue'
          titleSuffix={asOf ? <Text type='secondary' className='text-xs'>· as of {asOf}</Text> : undefined}
        />
      ),
      dataIndex: 'label',
      key: 'label',
      width: 260,
      onHeaderCell: () => ({ style: { ...SECTION_BAR_CELL_STYLE, paddingLeft: 16 } }),
      onCell: (row) => (row.kind === 'gap' ? { colSpan: funds.length + 2, style: GAP_CELL_STYLE } : {}),
      render: (_: unknown, row) => {
        if (row.kind === 'gap') return null
        if (row.kind === 'deal') {
          return (
            <span className='pl-4'>
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => openFormInTab(TABLE_DEALS, row.dealId ?? null, row.label)}
              >
                {row.label}
              </button>
            </span>
          )
        }
        const strong = row.kind !== 'figure' && row.kind !== 'account'
        return (
          <span>
            {strong ? <Text strong>{row.label}</Text> : row.label}
            <HelpTip title={row.help} />
          </span>
        )
      }
    }
    const fundColumns: ColumnsType<BudgetRow> = funds.map((f) => ({
      title: f.funds__name ?? 'Fund',
      key: f.funds__id,
      align: 'right' as const,
      onHeaderCell: () => ({ style: SECTION_BAR_CELL_STYLE }),
      onCell: figureCell,
      render: (_: unknown, row) => renderFigure(row, f.funds__id)
    }))
    const totalColumn: ColumnsType<BudgetRow>[number] = {
      title: 'Total',
      key: 'total',
      align: 'right' as const,
      onHeaderCell: () => ({ style: SECTION_BAR_CELL_STYLE }),
      onCell: figureCell,
      render: (_: unknown, row) => renderFigure(row, null)
    }
    // a single fund needs no Total column: it would repeat the fund's own
    return funds.length > 1 ? [labelColumn, ...fundColumns, totalColumn] : [labelColumn, ...fundColumns]
  }, [funds, renderFigure, openFormInTab, asOf])

  const rowClassName = useCallback((row: BudgetRow): string => {
    switch (row.kind) {
      case 'subtotal':
      case 'remaining':
        return 'bg-gray-50 border-t'
      case 'hero':
        return 'bg-blue-50'
      default:
        return ''
    }
  }, [])

  const emptyText = funds.length === 0 && !loading ? 'No funds to show — pick a fund in the ribbon.' : undefined

  return (
    <div ref={activateRef} className='h-full overflow-y-auto pr-1'>
      <ViewToolbar
        moduleKey='deal-pipeline-budget'
        searchEnabled={false}
        addButtonEnabled={false}
        onRefreshData={() => refresh(false)}
        leftSlot={
          <div className='flex items-center gap-2'>
            <span className='text-white text-xs'>
              Funds
              <HelpTip title={FUNDS_HELP} />
            </span>
            <FundSelect
              mode='multiple'
              size='small'
              value={selectedFundIds}
              onChange={(ids: string[] | null | undefined) => setSelectedFundIds(ids ?? [])}
              placeholder='Default funds'
              style={{ minWidth: 320 }}
              data-testid='budget-fund-select'
            />
          </div>
        }
      />
      <FormSection flush scrollable={false} loading={loading && !budget}>
        <ConfigProvider theme={STATEMENT_TABLE_THEME}>
          <Table<BudgetRow>
            size='small'
            pagination={false}
            loading={loading && !!budget}
            dataSource={rows}
            columns={columns}
            rowKey='key'
            rowClassName={rowClassName}
            tableLayout='auto'
            locale={emptyText ? { emptyText } : undefined}
            data-testid='budget-table'
          />
        </ConfigProvider>
      </FormSection>
    </div>
  )
}
